import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sparkles, ShieldCheck, Gavel } from 'lucide-react';

// Shared, reusable compliance notices -- EU MAR (ESMA's Jan 2026 finfluencer
// factsheet) and EU AI Act Art 50 transparency both call for these at the
// actual point of content creation/consumption, not just a one-time ToS
// acceptance at signup (Stocker already has that: RegisterPage.jsx's
// mandatory, server-tracked disclaimer checkbox -- see crud_legal_consent.py).
// See Technical_Docs/28_EU_REGULATORY_COMPLIANCE_FORWARD_PATH.md items 1-2
// and Technical_Docs/08_MASTER_TODO.md #48/#49 for the full reasoning.
// Kept as shared components (not one-off inline JSX per page) so the wording
// stays identical everywhere it's surfaced.

// `context`: 'general' (default, used in the composer/profile) vs
// 'subscription' -- the paid-group context specifically needs to state
// what's actually being sold (community access) alongside what it isn't
// (advice), since a paid relationship is exactly where the "investment
// advice for remuneration" question (AFM draft's Open Question #2) lives.
export const InvestmentDisclaimerNotice = ({ compact = false, context = 'general', className = '' }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-start gap-2.5 px-4 py-3 bg-amber-500/5 border border-amber-500/20 rounded-xl text-xs text-gray-400 ${className}`}
    >
      <AlertTriangle className="w-4 h-4 text-amber-400/80 flex-shrink-0 mt-0.5" />
      <p>
        {context === 'subscription'
          ? t('complianceNotices.investmentDisclaimer.subscriptionText')
          : compact
          ? t('complianceNotices.investmentDisclaimer.compactText')
          : t('complianceNotices.investmentDisclaimer.fullText')}{' '}
        {t('complianceNotices.investmentDisclaimer.readFull')}{' '}
        <Link to="/disclaimer" target="_blank" rel="noopener noreferrer" className="text-amber-300/90 hover:text-amber-200 underline">
          {t('complianceNotices.investmentDisclaimer.linkText')}
        </Link>
        .
      </p>
    </div>
  );
};

export const AIProcessingNotice = ({ variant = 'composer', className = '' }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-start gap-2.5 px-4 py-3 bg-violet-500/5 border border-violet-500/20 rounded-xl text-xs text-gray-400 ${className}`}
    >
      <Sparkles className="w-4 h-4 text-violet-400/80 flex-shrink-0 mt-0.5" />
      <p>
        {variant === 'composer'
          ? t('complianceNotices.aiProcessing.composerText')
          : t('complianceNotices.aiProcessing.defaultText')}
      </p>
    </div>
  );
};

// Ties Terms of Service §5 (Acceptable Use Policy) and §10 (Termination)
// together at the actual point of posting -- the ToS already states both
// (market manipulation / credibility-gaming / misleading content is
// prohibited, and violating the Terms can cost you your account), but the
// two sections previously required a reader to connect them across the
// document. Surfaced here rather than assuming the one-time signup
// acceptance is enough recall for what a specific post could risk.
export const CommunityGuidelinesNotice = ({ className = '' }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-start gap-2.5 px-4 py-3 bg-gray-500/5 border border-gray-500/20 rounded-xl text-xs text-gray-400 ${className}`}
    >
      <Gavel className="w-4 h-4 text-gray-400/80 flex-shrink-0 mt-0.5" />
      <p>
        {t('complianceNotices.communityGuidelines.text')}{' '}
        <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white underline">
          {t('complianceNotices.communityGuidelines.linkText')}
        </Link>{' '}
        {t('complianceNotices.communityGuidelines.suffix')}
      </p>
    </div>
  );
};

// For Elite-original Published News articles specifically -- these go
// through the ai_detection_score/flag pipeline stage plus mandatory human
// moderator review before publishing (see 25b_PUBLISHED_NEWS_DESIGN.md).
// Deliberately states the process, not a per-article score --
// NewsArticleFeedItem (the public response schema) never exposes the raw
// detection score/flag to readers.
//
// 2026-07-20 correction: this used to render "Screened by AI-content
// detection..." unconditionally for every elite_original article, regardless
// of whether real AI detection ran. The only provider ever wired in
// (get_ai_detection_provider(), app/utils/ai_content_detection/provider.py)
// is NoOpAIDetectionProvider, which always returns score=None/flag=None --
// no vendor is budget-approved yet. That made the badge a fabricated
// "clean" claim in the exact shape Art 50 transparency is supposed to
// prevent (falsely implying AI evaluation occurred). `active` now reflects
// the real, server-computed `ai_detection_active` field (true only once a
// real vendor is registered), so the AI-detection half of the claim is only
// ever made when it's actually true -- the human-moderation half stays
// unconditional because every submission genuinely does go through mandatory
// human review regardless of what the detector returns.
export const AIDetectionTrustBadge = ({ active = false, className = '' }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center gap-2 text-[11px] font-medium text-emerald-300/80 ${className}`}
    >
      <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
      <span>
        {active
          ? t('complianceNotices.aiDetection.active')
          : t('complianceNotices.aiDetection.inactive')}
      </span>
    </div>
  );
};
